import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation }            from 'react-router-dom';
import { toast }                                   from 'react-toastify';

import { MyContext }                       from '../../../../Context/MyContext';
import AminitiesBlock                      from './Aminities';
import LandAreaBlock                       from './LandAreaBlock';
import API                                 from '../../../../Api/api';

import {
  fieldVisible, fieldRequired,
  useDraft, RenderField,
  VideoBlock, LocationBlock, KVAddMore, PlanBlock,
  DocumentsBlock, CompanyProfileBlock,
  DraftModal, ICONS, IC,
} from './formUtils';

import './ProeprtyCreate.css';
import Dropdwon from './Dropdwon';
import { formValuesFromCustomDropdowns } from '../../../../utils/dropdownCustomValue';
import { getProjectDraftKey } from '../../../../utils/draftKeys';
import Loader from '../../../../Config/Loader';
import AppIcon from '../../../../Components/AppIcon';
import { handleUserKycApiError } from '../../../../utils/userKyc';
import PropertyOwnerNamePanel from '../../../../Admin/Components/PropertyOwnerNamePanel';

const projFieldVisible  = (f, typeId, catId, isOther) => fieldVisible(f, typeId, catId, isOther);
const projFieldRequired = (f, typeId, catId, isOther) => fieldRequired(f, typeId, catId, isOther);

// FieldsGrid — doc_field, radio, landtype, is_price_field உள்ளவற்றை skip
const FieldsGrid = ({ block, formData, onChange, errors, typeId, catId, isCatOther, projectInputs }) => {
  const allInputs = Object.values(projectInputs || {});
  const visible = allInputs
    .filter(f => f.block_key === block.block_key)
    .filter(f => f.is_show && !f.landtype && !f.is_price_field && !f.doc_field && f.type !== 'radio')
    .filter(f => projFieldVisible(f, typeId, catId, isCatOther))
    .sort((a, b) => (a.id || 0) - (b.id || 0));
  if (!visible.length) return null;
  return (
    <div className={`fields-grid${block.block_key === 'images' ? ' img-grid' : ''}`}>
      {visible.map(f => {
        const req = projFieldRequired(f, typeId, catId, isCatOther);
        return (
          <div key={f.field} className={f.type === 'textarea' ? 'f-full' : ''}>
            <RenderField fieldCfg={f} formData={formData} onChange={onChange} errors={errors} req={req}/>
          </div>
        );
      })}
    </div>
  );
};

const ProjectCreate = ({ isOwnProperty = false, draftScope = null, enableOwnerNamePanel = false }) => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const location       = useLocation();

  const {
    createProperty, updateProperty, saveDraftToBackend, properties, fetchPropertyById,
    projectBlocks, projectInputs,
    projectTypes, PROJECT_TYPE_SHOW, categorys,
  } = useContext(MyContext);

  const urlRole    = searchParams.get('role')   || null;
  const editId     = searchParams.get('editId') || null;
  const isEditMode = !!editId;

  const draftKey = useMemo(
    () => getProjectDraftKey(draftScope, { editId }),
    [draftScope, editId],
  );

  const {
    formData, setFormData, onChange,
    saveDraft, draftSaved, setDraftSaved,
    showDraftModal, restoreDraft, discardDraft, clearDraft,
  } = useDraft(draftKey, { enabled: !isEditMode });

  const [errors,      setErrors]      = useState({});
  const [toggles,     setToggles]     = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const safeInputs = (() => {
    if (!projectInputs) return {};
    if (Array.isArray(projectInputs)) {
      const obj = {};
      projectInputs.forEach(f => { if (f.field) obj[f.field] = f; });
      return obj;
    }
    return projectInputs;
  })();

  const safeBlocks = (projectBlocks || []).filter(b => b.is_project_show === true);

  // ── Edit mode restore ────────────────────────────────────
  useEffect(() => {
    if (!isEditMode) return;
    const mapToForm = (raw) => ({
      ...raw,
      ...(raw.extra_fields || {}),
      ...formValuesFromCustomDropdowns(raw),
      _prev_property_owner_number: raw.property_owner_number || '',
      type_id:   raw.type_id ?? 1,
      aminities: [...(raw.amenities_id || []), ...(raw.custom_amenities || [])],
      videos: (() => {
        if (raw.videos?.length) {
          return raw.videos.map((v, i) => ({
            id: Date.now() + i,
            thumbnail: v.thumbnail || null,
            video_link: v.video_link || '',
          }));
        }
        return (raw.youtube_links || []).map((link, i) => ({
          id: Date.now() + i, thumbnail: null, video_link: link,
        }));
      })(),
      shorts:    (raw.shorts_links || []).map((link, i) => ({ id: Date.now() + 1000 + i, short_link: link })),
      enable_video_section: !!(raw.youtube_links?.length || raw.shorts_links?.length),
      address:   raw.address || raw.location || '',
    });

    const loadData = async () => {
      // 1. Try local storage drafts
      try {
        const stored = JSON.parse(localStorage.getItem(draftKey));
        const arr    = Array.isArray(stored) ? stored : stored ? [stored] : [];
        const found  = arr.find(d => String(d.id) === String(editId));
        if (found) { setFormData(mapToForm(found)); return; }
      } catch {}

      // 2. Try context properties
      const existing = properties.find(p => String(p.id) === String(editId));
      if (existing) {
        setFormData(mapToForm(existing));
        return;
      }

      // 3. Fetch from backend
      try {
        const isStaff = location.pathname.startsWith('/admin') || location.pathname.startsWith('/partner');
        let fetched = null;
        if (isStaff) {
          try {
            const res = await API.get(`/admin/listings/${editId}`);
            fetched = res.data;
          } catch {
            fetched = null;
          }
        }
        if (!fetched) {
          fetched = await fetchPropertyById(editId);
        }
        if (fetched) setFormData(mapToForm(fetched));
        else toast.error('Failed to load project data.');
      } catch (err) {
        console.error('Failed to fetch project for edit:', err);
        toast.error('Failed to load project data.');
      }
    };

    loadData();
  }, [editId, properties.length]); // eslint-disable-line

  useEffect(() => {
    if (!isEditMode) return;
    setToggles({ enable_video_section: formData.enable_video_section ?? false });
  }, [formData.enable_video_section, isEditMode]); // eslint-disable-line

useEffect(() => {
  if (isOwnProperty && !isEditMode) {
    onChange('is_own_property', true);
  }
}, []);

  // ── Derived state ────────────────────────────────────────
  const typeId     = PROJECT_TYPE_SHOW ? (formData.type_id || null) : null;
  const catId      = formData.category_id || null;
  const catObj     = catId ? categorys.find(c => c.id === catId) : null;
  const isCatOther = catObj?.is_other === true;

  const projectCategorys  = categorys.filter(c => c.is_project === true);
  const filteredCategorys = PROJECT_TYPE_SHOW
    ? (typeId ? projectCategorys.filter(c => c.type_id.includes(typeId)) : [])
    : projectCategorys;
  const infoReady = PROJECT_TYPE_SHOW ? (!!typeId && !!catId) : !!catId;

  const handleTypeChange     = (f, v) => { setFormData(p => ({ ...p, type_id: v, category_id: '' })); setErrors({}); setDraftSaved(false); };
  const handleCategoryChange = (f, v) => { onChange('category_id', v); setErrors({}); setDraftSaved(false); };
  const isBlockEnabled = block =>
    !block.enable_toggle ||
    (block.toggle_field in toggles ? toggles[block.toggle_field] : block.default_enabled !== false);

  // ── Validate ─────────────────────────────────────────────
  // ✅ KEY FIX: range fields → range_from_field validate பண்ணு
  // f.field (e.g. "price") empty-ஆக இருந்தாலும் fail ஆகாது
  const validate = () => {
    const errs = {};

    if (PROJECT_TYPE_SHOW && !typeId) errs.type_id = 'Type is required';
    if (!catId) errs.category_id = 'Category is required';

    Object.values(safeInputs).forEach(f => {
      if (!f.is_show) return;
      if (!projFieldVisible(f, typeId, catId, isCatOther)) return;
      if (!projFieldRequired(f, typeId, catId, isCatOther)) return;

      if (f.range) {
        // ✅ Range field — from value மட்டும் required check
        const fromVal = formData[f.range_from_field];
        if (fromVal === undefined || fromVal === null || fromVal === '')
          errs[f.range_from_field] = `${f.label} (min) is required`;
        return;
      }

      if (f.type === 'file') {
        const val   = formData[f.field];
        const empty = !val || (Array.isArray(val) && val.length === 0);
        if (empty) errs[f.field] = `${f.label} is required`;
        return;
      }

      if (!formData[f.field] && formData[f.field] !== 0)
        errs[f.field] = `${f.label} is required`;
    });

    if (!formData.state_id) errs.state_id = 'State is required';
    if (!formData.city_id)  errs.city_id  = 'City is required';
    if (!formData.address)  errs.address  = 'Address is required';

    setErrors(errs);
    if (Object.keys(errs).length > 0) console.warn('Validation errors:', errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────
  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) {
    setTimeout(() => {
      document.querySelector('.error-msg')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return;
  }
  setSubmitting(true);
  try {
    const data = { ...formData, is_project: true };
    if (isEditMode) {
      await updateProperty(editId, data, urlRole, null);
      toast.success('Project updated!');
    } else {
      await createProperty(data, urlRole, null);
      clearDraft();
      toast.success('Project submitted!');
    }
    // REMOVED: All navigate() calls are gone. 
    // The Loader will handle the navigation via onDone.
  } catch (err) {
    setSubmitting(false);
    if (!handleUserKycApiError(err, {
      toast,
      navigate,
      pendingFlow: {
        context: 'project',
        role: urlRole,
        blockedAt: 'submit',
      },
    })) {
      toast.error(err.response?.data?.error || err.message || 'Something went wrong.');
    }
  }
};

  // ── Save Draft ───────────────────────────────────────────
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const data  = { ...formData, is_project: true };
      const saved = await saveDraftToBackend(data, urlRole, null);
      if (saved?.id && !formData.id) setFormData(p => ({ ...p, id: saved.id }));
      saveDraft();
      toast.success('Draft saved!');
      setDraftSaved(true);
    } catch {
      saveDraft();
      toast.info('Draft saved locally.');
    } finally {
      setSavingDraft(false);
    }
  };

  // ── Render blocks ────────────────────────────────────────
  const renderBlock = (block) => {
    const enabled = isBlockEnabled(block);
    const shared  = { formData, onChange, errors };
    switch (block.block_type) {
      case 'fields':
        return (
          <FieldsGrid
            block={block} {...shared}
            typeId={typeId} catId={catId} isCatOther={isCatOther}
            projectInputs={safeInputs}
          />
        );
      case 'land_area':
        return (
          <LandAreaBlock
            inputs={safeInputs} formData={formData} onChange={onChange}
            errors={errors} typeId={typeId} catId={catId}
            isOther={isCatOther} visibleFn={projFieldVisible} requiredFn={projFieldRequired}
          />
        );
      case 'video':
        return enabled
          ? <VideoBlock formData={formData} onChange={onChange}/>
          : <p className="disabled-hint">Enable to add videos.</p>;
      case 'location':
        return <LocationBlock {...shared}/>;
      case 'amenities':
        return <AminitiesBlock value={formData.aminities || []} onChange={onChange}/>;
      case 'key_value':
        return <KVAddMore block={block} {...shared}/>;
      case 'plan':
        return enabled
          ? <PlanBlock formData={formData} onChange={onChange}/>
          : <p className="disabled-hint">Enable to add plans.</p>;
      case 'documents':
        return <DocumentsBlock formData={formData} onChange={onChange}/>;
      case 'company_profile':
        return <CompanyProfileBlock formData={formData} onChange={onChange} errors={errors}/>;
      default:
        return null;
    }
  };

  // ── JSX ──────────────────────────────────────────────────
  return (
    <div className="create-page">
      {enableOwnerNamePanel && (
        <PropertyOwnerNamePanel
          name={formData.property_owner_name || ''}
          number={formData.property_owner_number || ''}
          onChange={({ name, number }) => {
            onChange('property_owner_name', name);
            onChange('property_owner_number', number);
          }}
        />
      )}
      {submitting && (
  <Loader 
    label={isEditMode ? 'Updating' : 'Submitting'} 
    onDone={() => {
      const path = window.location.pathname;
      if (path.startsWith('/admin')) navigate('/admin/properties', { replace: true });
      else if (path.startsWith('/partner')) navigate('/partner/properties', { replace: true });
      else navigate('/account', { replace: true });
    }} 
  />
)}
      {showDraftModal && !isEditMode && (
        <DraftModal onRestore={restoreDraft} onDiscard={discardDraft}/>
      )}

      <div className="page-header">
        <div className="breadcrumb">
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              const path = window.location.pathname;
              if (path.startsWith('/admin')) navigate('/admin/properties');
              else if (path.startsWith('/partner')) navigate('/partner/properties');
              else isEditMode ? navigate('/account') : navigate(-1);
            }}
          >
            Dashboard
          </span>
          {' › '}{isEditMode ? 'Edit Project' : 'Create Project'}
        </div>
        <h1 className="page-title">{isEditMode ? 'Edit Project' : 'Create Project'}</h1>
        {urlRole && (
          <div className="page-meta-badges">
            <span className="meta-badge">Role: {urlRole}</span>
          </div>
        )}
      </div>

      <form className="create-form" onSubmit={handleSubmit} noValidate>

        {/* ── Information block ── */}
        <div className="form-block">
          <div className="block-header">
            <div className="block-title-icon">{ICONS.info}</div>
            <h2 className="block-title">Project Information</h2>
          </div>
          <div className="block-body">
            <div className={PROJECT_TYPE_SHOW ? 'fields-grid tc-row' : 'tc-row-single'}>
              {PROJECT_TYPE_SHOW && (
                <Dropdwon
                  label="Project Type" field="type_id"
                  options={projectTypes} value={typeId || ''}
                  onChange={handleTypeChange} placeholder="Select type"
                  isrequired labelKey="type_name" valueKey="id"
                  error={errors.type_id}
                />
              )}
              {(!PROJECT_TYPE_SHOW || typeId) ? (
                <Dropdwon
                  label="Category" field="category_id"
                  options={filteredCategorys} value={catId || ''}
                  onChange={handleCategoryChange} placeholder="Select category"
                  isrequired labelKey="category_name" valueKey="id"
                  error={errors.category_id}
                />
              ) : (
                <div className="tc-cat-pending">
                  <span className="tc-cat-pending-text">← Select a type first</span>
                </div>
              )}
            </div>

            {infoReady && safeBlocks.find(b => b.block_key === 'information') && (
              <div style={{ marginTop: 20 }}>
                <FieldsGrid
                  block={safeBlocks.find(b => b.block_key === 'information')}
                  formData={formData} onChange={onChange} errors={errors}
                  typeId={typeId} catId={catId} isCatOther={isCatOther}
                  projectInputs={safeInputs}
                />
              </div>
            )}

            {!infoReady && (
              <p className="type-hint" style={{ marginTop: 12 }}>
                {!typeId && PROJECT_TYPE_SHOW ? (
                  <><AppIcon name="pointer" size={14} /> Select a project type.</>
                ) : (
                  <><AppIcon name="pointer" size={14} /> Select a category.</>
                )}
              </p>
            )}
          </div>
        </div>

        {/* ── Other blocks ── */}
        {safeBlocks.filter(b => b.block_key !== 'information').map(block => {
          if (block.block_type === 'land_area' && !infoReady) return null;
          const content = renderBlock(block);
          if (content === null) return null;
          return (
            <div key={block.id} className="form-block">
              <div className="block-header">
                <div className={`block-title-icon ${IC[block.block_icon] || ''}`}>
                  {ICONS[block.block_icon] || ICONS.info}
                </div>
                <h2 className="block-title">{block.block_name}</h2>
                {block.enable_toggle && (
                  <label className="form-block-toggle" title={isBlockEnabled(block) ? 'Section enabled' : 'Section disabled'}>
                    <input
                      type="checkbox"
                      checked={isBlockEnabled(block)}
                      onChange={() => {
                        const n = !isBlockEnabled(block);
                        setToggles((p) => ({ ...p, [block.toggle_field]: n }));
                        onChange(block.toggle_field, n);
                      }}
                    />
                    <span className="form-block-toggle-track" aria-hidden />
                    <span className="form-block-toggle-text">
                      {isBlockEnabled(block) ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                )}
              </div>
              <div className="block-body">{content}</div>
            </div>
          );
        })}

        {/* ── Submit row ── */}
        <div className="submit-row">
          {!isEditMode && (
            <button
              type="button"
              className={`draft-btn${draftSaved ? ' saved' : ''}`}
              onClick={handleSaveDraft}
              disabled={savingDraft}
            >
              <span className="draft-icon">
                {savingDraft ? <AppIcon name="loader" size={16} spin /> : draftSaved ? <AppIcon name="check" size={16} /> : <AppIcon name="save" size={16} />}
              </span>
              {savingDraft ? 'Saving…' : draftSaved ? 'Saved!' : 'Save Draft'}
            </button>
          )}
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting
              ? (isEditMode ? 'Updating…' : 'Submitting…')
              : (isEditMode ? 'Update Project' : 'Submit Project')
            }
          </button>
        </div>

      </form>
    </div>
  );
};

export default ProjectCreate;